// frontend/src/pages/LoginPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

import labImage from '../assets/login.png';
import '../styles/LoginPage.css';

export default function LoginPage() {
  const [form,      setForm]      = useState({ username: '', password: '' });
  const [errors,    setErrors]    = useState({ username: '', password: '' });
  const [loading,   setLoading]   = useState(false);
  const [showPwd,   setShowPwd]   = useState(false);
  const [authError, setAuthError] = useState({ show: false, message: '' });

  const { login } = useAuth();
  const navigate  = useNavigate();

  useEffect(() => {
    setForm({ username: '', password: '' });
    setErrors({ username: '', password: '' });
  }, []);

  const handleChange = e =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();

    const newErrors = { username: '', password: '' };
    let hasError = false;

    if (!form.username.trim()) {
      newErrors.username = 'Please enter a username';
      hasError = true;
    }
    if (!form.password.trim()) {
      newErrors.password = 'Please enter a password';
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) return;

    setLoading(true);
    try {
      await login(form.username, form.password);
      navigate('/');
    } catch (err) {

      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        null;

      const status = err?.response?.status;

      if (message && (
        message.toLowerCase().includes('disabled')  ||
        message.toLowerCase().includes('inactive')  ||
        message.toLowerCase().includes('blocked')   ||
        message.toLowerCase().includes('account')   ||
        status === 403
      )) {
        // ✅ Disabled account → modal popup, stays until user clicks OK manually
        setAuthError({ show: true, message });
      } else {
        // ✅ Wrong username or password → toast for 5500ms
        toast.error('Wrong username or password', {
          autoClose: 5500,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
        });
      }

    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp__root">

      <style>{`
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear { display: none; }
        input::-webkit-credentials-auto-fill-button { display: none; }
        .lp__error {
          color: #c0002a;
          font-size: 0.78rem;
          margin-top: 5px;
          margin-left: 4px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .lp__input--error {
          border: 1.5px solid #c0002a !important;
          background: #fff0f3 !important;
        }
        .auth__modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }
        .auth__modal {
          background: #fff;
          border-radius: 8px;
          padding: 28px 28px;
          max-width: 420px;
          width: 92%;
          box-shadow: 0 8px 30px rgba(0,0,0,0.18);
          text-align: center;
        }
        .auth__modal h4 {
          margin: 0 0 10px 0;
          font-size: 1.08rem;
          color: #c0002a;
        }
        .auth__modal p  { margin: 0 0 18px 0; color: #555; font-size: 0.92rem; }
        .auth__modal .auth__ok {
          background: #d6333e;
          color: #fff;
          border: none;
          padding: 9px 28px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.97rem;
        }
        .auth__modal .auth__ok:hover {
          background: #b52530;
        }
      `}</style>

      {/* ── LEFT: full-bleed lab photo ── */}
      <div className="lp__image-col">
        <img src={labImage} alt="Lab researcher" className="lp__image" />
      </div>

      {/* ── RIGHT: pink gradient form panel ── */}
      <div className="lp__form-col">
        <div className="lp__form-inner">

          <h1 className="lp__title">LOGIN</h1>
          <p className="lp__subtitle">Welcome Breast Cancer Detection</p>

          <form onSubmit={handleSubmit} noValidate className="lp__form" autoComplete="off">

            {/* Username */}
            <div className="lp__field">
              <label className="lp__label">Username:</label>
              <input
                className={`lp__input ${errors.username ? 'lp__input--error' : ''}`}
                type="text"
                name="username"
                placeholder="Enter the name"
                value={form.username}
                onChange={e => {
                  const value = e.target.value.replace(/[^a-zA-Z]/g, '');
                  setForm(p => ({ ...p, username: value }));
                  if (errors.username) setErrors(p => ({ ...p, username: '' }));
                }}
                maxLength={50}
                disabled={loading}
                autoFocus
                required
                autoComplete="off"
              />
              {errors.username && (
                <p className="lp__error">
                  <i className="fa-solid fa-circle-exclamation" />
                  {errors.username}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="lp__field">
              <label className="lp__label">Password:</label>
              <div className="lp__pwd-wrap">
                <input
                  className={`lp__input lp__input--pwd ${errors.password ? 'lp__input--error' : ''}`}
                  type={showPwd ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter the password"
                  value={form.password}
                  onChange={e => {
                    handleChange(e);
                    if (errors.password) setErrors(p => ({ ...p, password: '' }));
                  }}
                  maxLength={50}
                  disabled={loading}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="lp__eye-btn"
                  onClick={() => setShowPwd(v => !v)}
                  tabIndex={-1}
                  aria-label="Toggle password visibility"
                >
                  <i className={`fa-solid ${showPwd ? 'fa-eye' : 'fa-eye-slash'}`} />
                </button>
              </div>
              {errors.password && (
                <p className="lp__error">
                  <i className="fa-solid fa-circle-exclamation" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Login button */}
            <div className="lp__btn-row">
              <button type="submit" className="lp__btn" disabled={loading}>
                {loading
                  ? <><i className="fa-solid fa-spinner fa-spin me-2" />Signing in…</>
                  : 'Login'}
              </button>
            </div>

          </form>

        </div>
      </div>

      {/* ✅ Disabled account modal — NO autoClose, stays until user clicks OK */}
      {authError.show && (
        <div className="auth__modal-overlay" role="dialog" aria-modal="true">
          <div className="auth__modal">
            <h4>{authError.message}</h4>
            <p />
            <button
              type="button"
              className="auth__ok"
              onClick={() => setAuthError({ show: false, message: '' })}
            >
              OK
            </button>
          </div>
        </div>
      )}

    </div>
  );
}